import React, { useState, useEffect } from 'react';
import { useEvent } from '../context/EventContext';
import { useUserProfile } from '../hooks/useUserProfile';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/Button';
import AddInvoiceModal from '../components/AddInvoiceModal';
import StripePaymentModal from '../components/StripePaymentModal';
import enhancedApiClient from '../api/enhancedApiClient';

const Payments = () => {
  const { eventData, quotes, loading: eventLoading } = useEvent();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentQuotes, setPaymentQuotes] = useState([]);
  const [paymentInvoices, setPaymentInvoices] = useState([]);
  const [vendors, setVendors] = useState({});
  const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentOptionsModal, setShowPaymentOptionsModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', icon: 'default', budget: '', reserved: '' });
  const [selectedCategory, setSelectedCategory] = useState(null);

  const iconOptions = [
    { value: 'flowers', label: 'Flowers', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2a3 3 0 0 0-3 3c0 1.5 1 2.5 3 3 2-0.5 3-1.5 3-3a3 3 0 0 0-3-3z"/><path d="M19 12a3 3 0 0 1-3 3c-1.5 0-2.5-1-3-3 0.5-2 1.5-3 3-3a3 3 0 0 1 3 3z"/><path d="M12 19a3 3 0 0 1 3-3c1.5 0 2.5 1 3 3-0.5 2-1.5 3-3 3a3 3 0 0 1-3-3z"/><path d="M5 12a3 3 0 0 0 3 3c1.5 0 2.5-1 3-3-0.5-2-1.5-3-3-3a3 3 0 0 0-3 3z"/></svg> },
    { value: 'camera', label: 'Camera', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> },
    { value: 'cake', label: 'Cake', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s1-1 4-1 5 2 8 2 4-1 4-1"/><path d="M2 21h20"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/><path d="M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/><path d="M17 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/></svg> },
    { value: 'venue', label: 'Venue', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01"/><path d="M9 12v.01"/><path d="M9 15v.01"/><path d="M9 18v.01"/></svg> },
    { value: 'music', label: 'Music', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg> },
    { value: 'food', label: 'Food', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg> },
    { value: 'default', label: 'Other', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> }
  ];
  
  // Disable scroll when modals are open
  useEffect(() => {
    if (showPaymentModal || showPaymentOptionsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPaymentModal, showPaymentOptionsModal]);
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);
  
  // Load quotes and invoices
  useEffect(() => {
    const loadPaymentData = async () => {
      console.log('Budget: loadPaymentData called, eventId:', eventData?.eventId);
      if (!eventData?.eventId) {
        console.log('Budget: No eventId, skipping load');
        return;
      }
      setLoadingPayments(true);
      try {
        const allInvoices = await enhancedApiClient.getInvoices(eventData.eventId);
        console.log('Budget: Loaded invoices:', allInvoices.length, allInvoices);
        setPaymentQuotes(allInvoices.filter(item => item.invoiceType === 'quote'));
        setPaymentInvoices(allInvoices.filter(item => item.invoiceType === 'invoice'));
        
        // Load vendor names
        const vendorAssociations = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/vendors`);
        if (vendorAssociations.ok) {
          const associations = await vendorAssociations.json();
          const vendorMap = {};
          
          for (const association of associations) {
            if (!association.vendorId || association.vendorId === 'undefined') continue;
            try {
              const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${association.vendorId}`);
              if (vendorResponse.ok) {
                const vendorData = await vendorResponse.json();
                if (vendorData) {
                  vendorMap[association.vendorId] = vendorData.name?.S || vendorData.name || association.vendorId;
                }
              }
            } catch (e) {
              console.log('Budget: Failed to load vendor:', association.vendorId);
            }
          }
          setVendors(vendorMap);
        }

        // Load categories
        const catUrl = `https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/categories`;
        console.log('Budget: Fetching categories from:', catUrl);
        const catResponse = await fetch(catUrl);
        console.log('Budget: Categories response status:', catResponse.status);
        if (catResponse.ok) {
          const cats = await catResponse.json();
          console.log('Budget: Loaded categories:', cats.length, cats);
          setCategories(cats);
          
          // Check if we need to auto-open a category from navigation state
          if (location.state?.categoryId) {
            const category = cats.find(c => c.id === location.state.categoryId);
            if (category) {
              setSelectedCategory(category);
            }
          }
        }
      } catch (error) {
        console.error('Budget: Error loading payment data:', error);
      } finally {
        setLoadingPayments(false);
      }
    };
    loadPaymentData();
  }, [eventData?.eventId]);

  // Sync selected category with URL
  useEffect(() => {
    const catId = searchParams.get('category');
    if (catId && categories.length > 0) {
      const cat = categories.find(c => c.id === catId);
      setSelectedCategory(cat || null);
    } else if (!catId) {
      setSelectedCategory(null);
    }
  }, [searchParams, categories]);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!eventData?.eventId) return;
    console.log('Creating category for eventId:', eventData.eventId);
    try {
      const url = editingCategory
        ? `https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/categories/${editingCategory.id}`
        : `https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/categories`;
      console.log('POST URL:', url);
      const response = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });
      console.log('Response status:', response.status);
      if (response.ok) {
        const catResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/categories`);
        if (catResponse.ok) setCategories(await catResponse.json());
        setShowCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ name: '', icon: 'default', budget: '' });
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        alert('Failed to create category: ' + errorText);
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error: ' + error.message);
    }
  };

  if (eventLoading || profileLoading || loadingPayments) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading payments...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        backgroundColor: '#E8F4E8',
        padding: '32px 24px 140px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '16px'
      }}>
        <h1 style={{ 
          fontSize: '42px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          Budget
        </h1>
        
        {/* Payment Stats Cards */}
        <div style={{ 
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '16px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${Math.round((eventData?.budget || 25000) / 1000)}k <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>planned budget</span>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${Math.round(paymentQuotes.reduce((sum, q) => sum + (parseFloat(q.depositAmount) || 0), 0) / 1000)}k <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>quotes received</span>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${Math.round(paymentInvoices.reduce((sum, inv) => sum + (parseFloat(inv.depositAmount) || 0), 0) / 1000)}k <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>quotes accepted</span>
              </div>
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${Math.round(categories.reduce((sum, cat) => sum + (parseFloat(cat.reserved) || 0), 0) / 1000)}k <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>reserved</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        {!selectedCategory ? (
          // Budget Categories View
          <>
            <h2 style={{ 
              fontSize: '32px', 
              fontWeight: '400', 
              marginBottom: '24px',
              color: '#1F2937',
              fontFamily: 'Georgia, serif'
            }}>
              Budget Categories
            </h2>
            
            {categories.map(category => {
              const iconObj = iconOptions.find(i => i.value === category.icon);
              const categoryInvoices = [...paymentQuotes, ...paymentInvoices].filter(inv => inv.categoryId === category.id);
              const categoryQuotes = paymentQuotes.filter(q => q.categoryId === category.id);
              const categoryInvoicesOnly = paymentInvoices.filter(inv => inv.categoryId === category.id);
              // Only count accepted quotes (invoices) in total spent
              const totalSpent = categoryInvoicesOnly.reduce((sum, inv) => sum + (parseFloat(inv.depositAmount) || 0), 0);
              const reservedAmount = parseFloat(category.reserved || 0);
              const totalCommitted = totalSpent + reservedAmount;
              
              return (
                <div
                  key={category.id}
                  onClick={() => { setSelectedCategory(category); navigate(`/budget?category=${category.id}`); }}
                  style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    marginBottom: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ width: '56px', height: '56px', backgroundColor: '#E8F4E8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {iconObj?.icon || iconOptions.find(i => i.value === 'default').icon}
                      </div>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: '500', color: '#1F2937' }}>{category.name}</div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {categoryQuotes.length} {categoryQuotes.length === 1 ? 'quote' : 'quotes'}, {categoryInvoicesOnly.length} {categoryInvoicesOnly.length === 1 ? 'invoice' : 'invoices'}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937' }}>
                        ${totalCommitted.toLocaleString()} / ${parseFloat(category.budget || 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        {reservedAmount > 0 ? (
                          <span>${totalSpent.toLocaleString()} quoted + ${reservedAmount.toLocaleString()} reserved</span>
                        ) : (
                          <span>Spent / Budget</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategory(category);
                          setCategoryForm({ name: category.name, icon: category.icon, budget: category.budget, reserved: category.reserved || '' });
                          setShowCategoryModal(true);
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          backgroundColor: 'white',
                          color: '#6B9080',
                          border: '1px solid #6B9080',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button 
                onClick={() => setShowInvoiceModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                Add Invoice
              </button>
              <button 
                onClick={() => setShowQuoteModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                Add Quote
              </button>
              <button 
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryForm({ name: '', icon: 'default', budget: '', reserved: '' });
                  setShowCategoryModal(true);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '2px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                Add Budget Category
              </button>
              <button 
                onClick={() => setShowPaymentOptionsModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '2px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                View Payment Options
              </button>
            </div>
          </>
        ) : (
          // Category Detail View
          <>
            <button
              onClick={() => { setSelectedCategory(null); navigate('/budget'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                marginBottom: '24px'
              }}
            >
              ← Back to Categories
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
              <div style={{ width: '64px', height: '64px', backgroundColor: '#E8F4E8', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {iconOptions.find(i => i.value === selectedCategory.icon)?.icon || iconOptions.find(i => i.value === 'default').icon}
              </div>
              <div>
                <h2 style={{ fontSize: '32px', fontWeight: '400', color: '#1F2937', fontFamily: 'Georgia, serif', margin: 0 }}>
                  {selectedCategory.name}
                </h2>
                <div style={{ fontSize: '16px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  Budget: ${parseFloat(selectedCategory.budget || 0).toLocaleString()}
                  <button
                    onClick={() => {
                      setEditingCategory(selectedCategory);
                      setCategoryForm({ name: selectedCategory.name, icon: selectedCategory.icon, budget: selectedCategory.budget, reserved: selectedCategory.reserved || '' });
                      setShowCategoryModal(true);
                    }}
                    style={{ padding: '4px 12px', backgroundColor: '#F3F4F6', color: '#6B7280', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Edit Budget
                  </button>
                </div>
              </div>
            </div>

            {/* Quotes */}
            <div style={{ marginBottom: '48px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '400', marginBottom: '16px', color: '#1F2937', fontFamily: 'Georgia, serif' }}>
                Quotes
              </h3>
              {paymentQuotes.filter(q => q.categoryId === selectedCategory.id).length > 0 ? (
                paymentQuotes.filter(q => q.categoryId === selectedCategory.id).map(quote => {
                  const getQuoteStatusBadge = () => {
                    if (quote.status === 'accepted') {
                      return { text: 'ACCEPTED', bg: '#D1FAE5', color: '#065F46' };
                    } else if (quote.status === 'rejected') {
                      return { text: 'REJECTED', bg: '#FEE2E2', color: '#991B1B' };
                    } else {
                      return { text: 'PENDING', bg: '#FEF3C7', color: '#92400E' };
                    }
                  };
                  const statusBadge = getQuoteStatusBadge();
                  
                  return (
                  <div key={quote.id} style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                          <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937' }}>
                            {quote.name || `Quote #${quote.id?.split('-')[1] || 'Unknown'}`}
                          </div>
                          <span style={{ padding: '4px 12px', backgroundColor: statusBadge.bg, color: statusBadge.color, borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                            {statusBadge.text}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {vendors[quote.vendorId] || quote.vendorId}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                          <Button variant="outline" size="small" onClick={() => { setSelectedQuote(quote); setShowQuoteDetailsModal(true); }}>
                            View
                          </Button>
                          {quote.pdfUrl && (
                            <Button variant="outline" size="small" onClick={() => window.open(quote.pdfUrl, '_blank')}>
                              View Document
                            </Button>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937' }}>
                          ${quote.depositAmount || 0}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {(() => {
                            if (!quote.dueDate) return 'No due date';
                            try {
                              const date = new Date(quote.dueDate);
                              if (isNaN(date.getTime())) return 'Invalid date';
                              return `Due ${date.toLocaleDateString()}`;
                            } catch {
                              return 'Invalid date';
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })
              ) : (
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', textAlign: 'center', color: '#6B7280' }}>
                  No quotes in this category
                </div>
              )}
            </div>

            {/* Invoices */}
            <div>
              <h3 style={{ fontSize: '24px', fontWeight: '400', marginBottom: '16px', color: '#1F2937', fontFamily: 'Georgia, serif' }}>
                Invoices
              </h3>
              {paymentInvoices.filter(inv => inv.categoryId === selectedCategory.id).length > 0 ? (
                paymentInvoices.filter(inv => inv.categoryId === selectedCategory.id).map(invoice => {
                  const getInvoiceStatusBadge = () => {
                    if (invoice.status === 'paid') {
                      return { text: 'PAID', bg: '#D1FAE5', color: '#065F46' };
                    } else if (invoice.status === 'overdue') {
                      return { text: 'OVERDUE', bg: '#FEE2E2', color: '#991B1B' };
                    } else {
                      return { text: 'PENDING', bg: '#FEF3C7', color: '#92400E' };
                    }
                  };
                  const statusBadge = getInvoiceStatusBadge();
                  
                  return (
                  <div key={invoice.id} style={{
                    backgroundColor: 'white',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                          <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937' }}>
                            {invoice.name || `Invoice #${invoice.id?.split('-')[1] || 'Unknown'}`}
                          </div>
                          <span style={{ padding: '4px 12px', backgroundColor: statusBadge.bg, color: statusBadge.color, borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                            {statusBadge.text}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                          {vendors[invoice.vendorId] || invoice.vendorId}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <Button variant="outline" size="small" onClick={() => { setSelectedQuote(invoice); setShowQuoteDetailsModal(true); }}>
                            View
                          </Button>
                          {invoice.pdfUrl && (
                            <Button variant="outline" size="small" onClick={() => window.open(invoice.pdfUrl, '_blank')}>
                              View Document
                            </Button>
                          )}
                          {invoice.status !== 'paid' && (
                            <Button variant="primary" size="small" onClick={() => { setSelectedInvoice(invoice); setShowPaymentModal(true); }}>
                              Make a Payment
                            </Button>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937' }}>
                          ${invoice.depositAmount || invoice.total || 0}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          {(() => {
                            if (!invoice.dueDate) return 'No due date';
                            try {
                              const date = new Date(invoice.dueDate);
                              if (isNaN(date.getTime())) return 'Invalid date';
                              return `Due ${date.toLocaleDateString()}`;
                            } catch {
                              return 'Invalid date';
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })
              ) : (
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', textAlign: 'center', color: '#6B7280' }}>
                  No invoices in this category
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button 
                onClick={() => setShowInvoiceModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                Add Invoice
              </button>
              <button 
                onClick={() => setShowQuoteModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                Add Quote
              </button>
            </div>
          </>
        )}
        
        <AddInvoiceModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          type="quote"
          eventId={eventData?.eventId}
          onSubmit={async (data) => {
            try {
              let pdfUrl = '';
              
              console.log('Quote submission - data.pdfFile:', data.pdfFile);
              if (data.pdfFile) {
                const uploadResult = await enhancedApiClient.uploadFile(data.pdfFile);
                console.log('Upload result:', uploadResult);
                pdfUrl = uploadResult.fileUrl;
                console.log('pdfUrl set to:', pdfUrl);
              }
              
              const quote = {
                eventId: eventData?.eventId,
                invoiceType: 'quote',
                name: data.name,
                vendorId: data.vendor,
                categoryId: data.categoryId,
                total: data.totalAmount ? parseFloat(data.totalAmount) : 0,
                depositAmount: data.depositAmount ? parseFloat(data.depositAmount) : 0,
                dueDate: data.dueDate,
                pdfUrl: pdfUrl,
                status: 'pending',
                createdAt: new Date().toISOString()
              };
              
              console.log('Creating quote with:', quote);
              await enhancedApiClient.createQuote(quote);
              setShowQuoteModal(false);
              setSuccessMessage('Quote created successfully!');
              setShowSuccessModal(true);
              // Reload quotes
              const allInvoices = await enhancedApiClient.getInvoices(eventData.eventId);
              setPaymentQuotes(allInvoices.filter(item => item.invoiceType === 'quote'));
              // Reload invoices
              setPaymentInvoices(allInvoices.filter(item => item.invoiceType === 'invoice'));
            } catch (error) {
              console.error('Error creating quote:', error);
              alert('Failed to create quote. Please try again.');
            }
          }}
        />
        
        <AddInvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          type="invoice"
          eventId={eventData?.eventId}
          onSubmit={async (data) => {
            try {
              let pdfUrl = '';
              
              if (data.pdfFile) {
                const uploadResult = await enhancedApiClient.uploadFile(data.pdfFile);
                pdfUrl = uploadResult.fileUrl;
              }
              
              const invoice = {
                eventId: eventData?.eventId,
                invoiceType: 'invoice',
                name: data.name,
                vendorId: data.vendor,
                categoryId: data.categoryId,
                total: data.totalAmount ? parseFloat(data.totalAmount) : 0,
                depositAmount: data.depositAmount ? parseFloat(data.depositAmount) : 0,
                dueDate: data.dueDate,
                pdfUrl: pdfUrl,
                status: 'pending',
                createdAt: new Date().toISOString()
              };
              
              await enhancedApiClient.createInvoice(invoice);
              setShowInvoiceModal(false);
              setSuccessMessage('Invoice created successfully!');
              setShowSuccessModal(true);
              // Reload invoices
              const allInvoices = await enhancedApiClient.getInvoices(eventData.eventId);
              setPaymentQuotes(allInvoices.filter(item => item.invoiceType === 'quote'));
              setPaymentInvoices(allInvoices.filter(item => item.invoiceType === 'invoice'));
            } catch (error) {
              console.error('Error creating invoice:', error);
              alert('Failed to create invoice. Please try again.');
            }
          }}
        />
        
        {/* Quote/Invoice Details Modal */}
        {showQuoteDetailsModal && selectedQuote && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '550px', maxHeight: '85vh', overflowY: 'auto' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '500', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
                {selectedQuote?.invoiceType === 'quote' ? 'Quote' : 'Invoice'} Details
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Name</div>
                <div style={{ fontSize: '16px', color: '#1F2937' }}>{selectedQuote.name || 'Unnamed'}</div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Vendor</div>
                <div style={{ fontSize: '16px', color: '#1F2937' }}>{vendors[selectedQuote.vendorId] || selectedQuote.vendorName || selectedQuote.vendorId}</div>
              </div>

              {selectedQuote.description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Description</div>
                  <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>{selectedQuote.description}</div>
                </div>
              )}

              {selectedQuote.total && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Total Amount</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937' }}>${parseFloat(selectedQuote.total).toLocaleString()}</div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Deposit Amount</div>
                <div style={{ fontSize: '16px', color: '#1F2937' }}>${selectedQuote.depositAmount || 0}</div>
              </div>

              {selectedQuote.lineItems && selectedQuote.lineItems.length > 0 && selectedQuote.lineItems[0].description && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Line Items</div>
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px', gap: '8px', padding: '8px 12px', backgroundColor: '#F9FAFB', fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>
                      <div>Item</div><div>Units</div><div>Amount</div>
                    </div>
                    {selectedQuote.lineItems.map((item, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px', gap: '8px', padding: '8px 12px', borderTop: '1px solid #E5E7EB', fontSize: '14px' }}>
                        <div>{item.description}</div><div>{item.units}</div><div>${item.amount}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Due Date</div>
                  <div style={{ fontSize: '16px', color: '#1F2937' }}>{selectedQuote.dueDate ? new Date(selectedQuote.dueDate).toLocaleDateString() : 'No due date'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Status</div>
                  <div style={{ fontSize: '16px', color: '#1F2937', textTransform: 'capitalize' }}>{selectedQuote.status || 'Pending'}</div>
                </div>
              </div>

              {selectedQuote.notes && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Notes</div>
                  <div style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.5' }}>{selectedQuote.notes}</div>
                </div>
              )}

              {selectedQuote.eventId && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>Event</div>
                  <button onClick={() => { setShowQuoteDetailsModal(false); navigate(`/budget`); }}
                    style={{ fontSize: '14px', color: '#6B9080', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                    {eventData?.clientName || eventData?.name || 'View Event'}
                  </button>
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Created {selectedQuote.createdAt ? new Date(selectedQuote.createdAt).toLocaleDateString() : 'Unknown'}</div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {selectedQuote.pdfUrl && (
                  <button onClick={() => window.open(selectedQuote.pdfUrl, '_blank')}
                    style={{ padding: '12px 24px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    View Document
                  </button>
                )}
                <button onClick={() => setShowQuoteDetailsModal(false)}
                  style={{ padding: '12px 24px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Payment Modal */}
        {showPaymentModal && selectedInvoice && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowPaymentModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                width: '600px',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{
                fontSize: '24px',
                fontWeight: '500',
                color: '#1F2937',
                marginBottom: '20px',
                fontFamily: 'Georgia, serif'
              }}>
                Payment Options
              </h3>
              
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  backgroundColor: '#F9FAFB',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  cursor: 'pointer',
                  border: '2px solid transparent'
                }}
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowStripeModal(true);
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#6B9080'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                >
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '8px' }}>Pay with Credit Card</div>
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>Secure payment powered by Stripe</div>
                </div>
                
                <div style={{
                  backgroundColor: '#F9FAFB',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  cursor: 'pointer',
                  border: '2px solid transparent'
                }}
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowBankTransferModal(true);
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#6B9080'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                >
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '8px' }}>Bank Transfer</div>
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>Transfer directly to Everset's bank account</div>
                </div>
                
                <div style={{
                  backgroundColor: '#F9FAFB',
                  padding: '20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: '2px solid transparent'
                }}
                onClick={() => {
                  setShowPaymentModal(false);
                  setSuccessMessage('Our representative will contact you shortly to discuss financing options.');
                  setShowSuccessModal(true);
                }}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#6B9080'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                >
                  <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '8px' }}>Everset Payment Plan</div>
                  <div style={{ fontSize: '14px', color: '#6B7280' }}>Have our representative contact you to discuss finance options</div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: 'white',
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Payment Options Modal */}
        {showPaymentOptionsModal && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowPaymentOptionsModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                width: '650px',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPaymentOptionsModal(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '500',
                color: '#1F2937',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                Payment Options with Everset
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>
                Choose the payment method that works best for you
              </p>
              
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  backgroundColor: '#E8F4E8',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '1px solid #6B9080'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937' }}>Everset Payment Plan</div>
                  </div>
                  <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5', marginBottom: '16px' }}>
                    Spread your wedding costs over time with flexible financing options. Our finance specialists will work with you to create a payment plan that fits your budget. Enjoy competitive rates, flexible terms, and personalized service.
                  </p>
                  <button
                    onClick={() => {
                      setShowPaymentOptionsModal(false);
                      setSuccessMessage('Thank you for your interest! Our finance specialist will contact you within 24 hours to discuss your options.');
                      setShowSuccessModal(true);
                    }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6B9080',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Enquire About Financing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Stripe Payment Modal */}
        <StripePaymentModal
          isOpen={showStripeModal}
          onClose={() => setShowStripeModal(false)}
          invoice={selectedInvoice}
          vendor={selectedInvoice ? vendors[selectedInvoice.vendorId] : ''}
        />
        
        {/* Bank Transfer Modal */}
        {showBankTransferModal && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowBankTransferModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                width: '500px',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{
                fontSize: '24px',
                fontWeight: '500',
                color: '#1F2937',
                marginBottom: '8px',
                fontFamily: 'Georgia, serif'
              }}>
                Bank Transfer Details
              </h3>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>
                Transfer funds directly to Everset's bank account
              </p>
              
              <div style={{
                backgroundColor: '#F9FAFB',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Account Name</div>
                  <div style={{ fontSize: '16px', color: '#1F2937', fontWeight: '500' }}>Everset Limited</div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Bank</div>
                  <div style={{ fontSize: '16px', color: '#1F2937', fontWeight: '500' }}>ANZ Bank New Zealand</div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Account Number</div>
                  <div style={{ fontSize: '16px', color: '#1F2937', fontWeight: '500' }}>01-0234-0567890-00</div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Reference</div>
                  <div style={{ fontSize: '16px', color: '#1F2937', fontWeight: '500' }}>
                    {(() => {
                      const idPart = selectedInvoice?.id?.split('-')[1] || '0';
                      return 'EV' + parseInt(idPart).toString(16).toUpperCase().slice(-8);
                    })()}
                  </div>
                </div>
                
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Amount</div>
                  <div style={{ fontSize: '20px', color: '#1F2937', fontWeight: 'bold' }}>
                    ${selectedInvoice?.depositAmount || selectedInvoice?.total || 0}
                  </div>
                </div>
              </div>
              
              <div style={{
                backgroundColor: '#FEF3C7',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '24px',
                display: 'flex',
                gap: '12px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <div style={{ fontSize: '14px', color: '#92400E', lineHeight: '1.5' }}>
                  Please include the reference number in your transfer so we can match your payment to the correct invoice.
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowBankTransferModal(false)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Category Modal */}
        {showCategoryModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              width: '500px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', marginBottom: '24px', fontFamily: 'Georgia, serif' }}>
                {editingCategory ? 'Edit Category' : 'Add Budget Category'}
              </h2>
              <form onSubmit={handleCategorySubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Name</label>
                  <input
                    type="text"
                    required
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Venue, Florals, etc."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Icon</label>
                  <select
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {iconOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Budget ($)</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={categoryForm.budget}
                    onChange={(e) => setCategoryForm({ ...categoryForm, budget: e.target.value })}
                    placeholder="5000"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Reserved Budget ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={categoryForm.reserved}
                    onChange={(e) => setCategoryForm({ ...categoryForm, reserved: e.target.value })}
                    placeholder="0"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                      outline: 'none'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                    For consumption-based items (e.g., bar tab, per-person charges)
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryModal(false);
                      setEditingCategory(null);
                      setCategoryForm({ name: '', icon: 'default', budget: '', reserved: '' });
                    }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: '#6B7280',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6B9080',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    {editingCategory ? 'Save Changes' : 'Add Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              width: '400px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#6B9080',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '500',
                color: '#1F2937',
                marginBottom: '8px'
              }}>
                Success!
              </h3>
              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                marginBottom: '24px'
              }}>
                {successMessage}
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;